import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
    Video, UploadCloud, BrainCircuit, X, Film, ThumbsUp, 
    ThumbsDown, Lightbulb, TrendingUp, Star, Users, Trash2, AlertTriangle,
    Download, FileText, CheckCircle, Loader2, ScanEye, MonitorPlay, Aperture, Scissors,
    Zap, Target, BarChart2, Activity, Globe, ChevronRight, ShieldAlert, Rocket, Clapperboard
} from 'lucide-react';

// Shared UI
import { 
    AppButton 
} from '../../components/SharedUI';

// Services
import { generateDetailedVideoAnalysisLLM } from '../../services/aiService';

// Firebase Imports
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';

// --- STYLED COMPONENTS ---

const TimecodeDisplay = ({ frameCount = 0 }) => {
    // Fake timecode generator based on frame count (assuming 24fps roughly)
    const totalFrames = frameCount * 24; 
    const hours = Math.floor(totalFrames / (3600 * 24));
    const minutes = Math.floor((totalFrames % (3600 * 24)) / (60 * 24));
    const seconds = Math.floor((totalFrames % (60 * 24)) / 24);
    const frames = Math.floor(totalFrames % 24);

    const pad = (n) => String(n).padStart(2, '0');
    
    return (
        <div className="font-mono text-cyan-400 text-lg tracking-widest bg-black/50 px-3 py-1 rounded border border-cyan-900/30 backdrop-blur-md shadow-lg">
            {pad(hours)}:{pad(minutes)}:{pad(seconds)}:{pad(frames)}
        </div>
    );
};

const InspectorItem = ({ label, children, icon: Icon }) => (
    <div className="border-b border-zinc-800 pb-4 last:border-0">
        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            {Icon && <Icon size={10} />} {label}
        </p>
        <div className="text-zinc-300 text-sm leading-relaxed">
            {children}
        </div>
    </div>
);

// --- PREMIUM OVERLAY COMPONENT ---
const PremiumVideoOverlay = ({ isVisible, currentStepIndex, progressPercent }) => {
    if (!isVisible) return null;

    const steps = [
        "Ingesting Video Source & Metadata...",
        "Extracting Keyframes (Computer Vision)...",
        "Deconstructing Visual Narrative...",
        "Identifying Emotional Beats & Pacing...",
        "Detecting 'Money Shot' Candidates...",
        "Generating Editor Shot Lists..."
    ];

    const displayProgress = Math.max(5, progressPercent);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-cyan-500 blur-2xl opacity-20 animate-pulse rounded-full"></div>
                        <ScanEye className="w-20 h-20 text-white relative z-10 animate-pulse" />
                    </div>
                </div>

                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-cyan-600 to-blue-500 transition-all duration-300 ease-out"
                        style={{ width: `${displayProgress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        let statusIcon;
                        let statusColor;
                        
                        if (index < currentStepIndex) {
                            statusIcon = <CheckCircle size={14} />;
                            statusColor = "text-cyan-500 opacity-50";
                        } else if (index === currentStepIndex) {
                            statusIcon = <Loader2 size={14} className="animate-spin" />;
                            statusColor = "text-white font-bold";
                        } else {
                            statusIcon = <div className="w-3 h-3 rounded-full border border-zinc-700" />;
                            statusColor = "text-zinc-600";
                        }

                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${statusColor}`}>
                                <div className="w-5 flex justify-center shrink-0">{statusIcon}</div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- FRAME EXTRACTION ENGINE ---
const extractFramesFromVideo = (videoSource, numFrames = 15, setProgress) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous"; 

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const frames = [];

        video.onloadedmetadata = () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const duration = video.duration;
            if (!isFinite(duration) || duration === 0) {
                reject(new Error("Cannot determine video duration."));
                return;
            }
            const interval = duration / numFrames;
            let currentTime = 0;
            let framesExtracted = 0;

            const captureFrame = () => {
                if (framesExtracted >= numFrames) {
                    if (typeof videoSource !== 'string') URL.revokeObjectURL(video.src);
                    resolve(frames);
                    return;
                }
                video.currentTime = currentTime;
            };

            video.onseeked = () => {
                try {
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);
                    frames.push(canvas.toDataURL('image/jpeg', 0.8));
                    framesExtracted++;
                    if (setProgress) setProgress(framesExtracted);
                    currentTime += interval;
                    if (currentTime > duration) currentTime = duration;
                    captureFrame();
                } catch(e) {
                     reject(new Error("CORS policy prevented frame extraction."));
                }
            };
            
            video.onerror = () => { 
                if (typeof videoSource !== 'string') URL.revokeObjectURL(video.src);
                reject(new Error("Error loading video.")); 
            };
            
            captureFrame();
        };

        if (typeof videoSource === 'string') {
            video.src = videoSource;
        } else {
            video.src = URL.createObjectURL(videoSource);
        }
        video.load();
    });
};

// --- MAIN COMPONENT ---
const VideoAnalysisTab = ({ filmData, setNotification, saveFilmData, db, storage, credits, deductCredit }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0); 
  const [loadingStepIndex, setLoadingStepIndex] = useState(0); 
  
  const [activeFrameIndex, setActiveFrameIndex] = useState(0);
  const [activeSpotTab, setActiveSpotTab] = useState('spot60');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeIntelTab, setActiveIntelTab] = useState('overview');
  
  const scrollContainerRef = useRef(null);

  // Memoized data
  const analysis = useMemo(() => filmData?.video_analysis || null, [filmData?.video_analysis]);
  const isYoutubeLink = useMemo(() => videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be"), [videoUrl]);

  // Load existing frames
  useEffect(() => {
    if (filmData?.video_keyframes_urls && filmData.video_keyframes_urls.length > 0) {
        setFrames(filmData.video_keyframes_urls);
    }
    if (filmData?.video_analysis?.SpotBreakdowns) {
        setActiveSpotTab(Object.keys(filmData.video_analysis.SpotBreakdowns)[0] || 'spot60');
    }
  }, [filmData]);

  // --- KEYBOARD NAVIGATION & AUTO-SCROLL ---
  useEffect(() => {
    const handleKeyDown = (e) => {
        if (frames.length === 0) return;
        if (e.key === 'ArrowRight') {
            setActiveFrameIndex(prev => Math.min(frames.length - 1, prev + 1));
        } else if (e.key === 'ArrowLeft') {
            setActiveFrameIndex(prev => Math.max(0, prev - 1));
        }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [frames]);

  // Auto-scroll logic
  useEffect(() => {
      if (scrollContainerRef.current) {
          const button = scrollContainerRef.current.children[activeFrameIndex];
          if (button) {
              button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
      }
  }, [activeFrameIndex]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      setVideoUrl("");
      setFrames([]);
    } else {
      setNotification({ type: 'error', message: 'Please upload a valid video file.' });
    }
  };

  const robustImageUpload = async (base64String, path, fileName) => {
    try {
        const response = await fetch(base64String);
        const blob = await response.blob();
        const fullPath = `${path}/${fileName}`;
        const storageRef = ref(storage, fullPath);
        await uploadBytes(storageRef, blob);
        return await getDownloadURL(storageRef);
    } catch (error) {
        return null;
    }
  };

  const handleAnalyze = async () => {
    if (credits <= 0) {
        setNotification({ type: 'error', message: 'Insufficient credits.' });
        return;
    }

    const source = videoFile || videoUrl;
    if (!source || isYoutubeLink) {
      setNotification({ type: 'error', message: 'Please upload a valid video file.' });
      return;
    }

    setLoading(true);
    setLoadingStepIndex(0); 
    setFrames([]);
    setProgress(0);
    
    try {
      setLoadingStepIndex(1);
      const extractedFrames = await extractFramesFromVideo(source, 15, (num) => setProgress((num/15)*100));
      setFrames(extractedFrames); 
      
      setLoadingStepIndex(2);
      await new Promise(r => setTimeout(r, 1000));
      
      setLoadingStepIndex(3);
      const result = await generateDetailedVideoAnalysisLLM(extractedFrames, filmData.genre || 'General', filmData.title || 'Untitled');
      
      setLoadingStepIndex(4);
      
      let keyframeUrls = [];
      if (storage && filmData.id) {
        const uploadPromises = extractedFrames.map((frame, i) => robustImageUpload(frame, `${filmData.id}/video-frames`, `keyframe_${i}_${Date.now()}.jpg`));
        keyframeUrls = (await Promise.all(uploadPromises)).filter(url => url !== null);
      }

      setLoadingStepIndex(5);
      await new Promise(r => setTimeout(r, 800));

      await saveFilmData({ ...filmData, video_analysis: result, video_keyframes_urls: keyframeUrls });
      await deductCredit(1);
      setNotification({ type: 'success', message: 'Analysis complete.' });

    } catch (error) {
      setNotification({ type: 'error', message: `Analysis failed: ${error.message}` });
    } finally {
      setLoading(false);
      setProgress(0);
      setLoadingStepIndex(0);
    }
  };

  // --- PDF EXPORT LOGIC ---
  const handleExportShotList = () => {
    try {
        if (!window.jspdf) {
            setNotification({ type: 'error', message: 'PDF generator not loaded.' });
            return;
        }
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        const margin = 20;
        let y = 20;

        doc.setFillColor(24, 24, 27); // Dark header
        doc.rect(0, 0, 210, 40, 'F');
        
        doc.setFontSize(18);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.text("EDITOR SHOT LIST", margin, 20);
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`${filmData.title || 'Untitled Project'}  |  AI Analysis`, margin, 30);
        
        y = 50;
        doc.setTextColor(0, 0, 0);

        const spots = analysis?.SpotBreakdowns || {};
        Object.entries(spots).forEach(([key, data]) => {
            if (y > 250) { doc.addPage(); y = 20; }
            
            const spotTitle = key.replace('spot', '') + ' Second Spot';
            
            doc.setFillColor(6, 182, 212); // Cyan
            doc.rect(margin, y, 4, 10, 'F');
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(spotTitle.toUpperCase(), margin + 8, y + 8);
            y += 15;

            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(80, 80, 80);
            doc.text(`OBJECTIVE: ${data.goal}`, margin, y);
            y += 10;

            doc.setFont("helvetica", "normal");
            doc.setTextColor(0, 0, 0);
            
            data.shots.forEach((shot, i) => {
                const tc = shot?.timecode ?? null;
                const desc = shot?.description ?? (typeof shot === 'string' ? shot : '');
                const prefix = tc ? `${String(i+1).padStart(2,'0')}  [${tc}]  ` : `${i + 1}. `;
                const text = `${prefix}${desc}`;
                const lines = doc.splitTextToSize(text, 170);
                
                if (y + (lines.length * 6) > 280) {
                    doc.addPage();
                    y = 20;
                }
                
                doc.text(lines, margin, y);
                y += (lines.length * 6) + 4;
            });
            y += 15;
        });

        doc.save(`${filmData.title || 'Film'}_Shot_List.pdf`);
        setNotification({ type: 'success', message: 'Shot list exported.' });
    } catch (error) {
        console.error("PDF Export Error:", error);
        setNotification({ type: 'error', message: 'Failed to generate PDF.' });
    }
  };

  // --- TRAILER ANALYSIS REPORT EXPORT ---
  const handleExportAnalysisReport = () => {
    try {
        if (!window.jspdf) {
            setNotification({ type: 'error', message: 'PDF generator not loaded.' });
            return;
        }
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        const PW = 210; // page width mm
        const PH = 297; // page height mm
        const margin = 20;
        const contentW = PW - margin * 2;
        let y = 0;

        // --- HELPERS ---
        const checkPage = (needed = 10) => {
            if (y + needed > PH - 15) { doc.addPage(); y = 20; }
        };

        const sectionHeader = (label, r, g, b) => {
            checkPage(14);
            doc.setFillColor(r, g, b);
            doc.rect(margin, y, contentW, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text(label.toUpperCase(), margin + 3, y + 5.5);
            y += 12;
            doc.setTextColor(30, 30, 30);
        };

        const bodyText = (text, indent = 0, size = 9) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(size);
            doc.setTextColor(50, 50, 50);
            const lines = doc.splitTextToSize(text, contentW - indent);
            checkPage(lines.length * 5 + 2);
            doc.text(lines, margin + indent, y);
            y += lines.length * 5 + 2;
        };

        const labeledRow = (label, value) => {
            checkPage(8);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(label.toUpperCase(), margin, y);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 30, 30);
            const lines = doc.splitTextToSize(value || '—', contentW - 36);
            doc.text(lines, margin + 36, y);
            y += lines.length * 5 + 3;
        };

        const scoreBar = (label, score, r, g, b) => {
            checkPage(10);
            const barW = contentW - 50;
            const filled = Math.round((score / 100) * barW);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(50, 50, 50);
            doc.text(label, margin, y + 3.5);
            // bg track
            doc.setFillColor(220, 220, 220);
            doc.roundedRect(margin + 36, y, barW, 5, 1, 1, 'F');
            // filled
            doc.setFillColor(r, g, b);
            if (filled > 0) doc.roundedRect(margin + 36, y, filled, 5, 1, 1, 'F');
            // score text
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(r, g, b);
            doc.text(`${score}`, margin + 36 + barW + 4, y + 3.5);
            y += 9;
        };

        const buzzColor = (factor) => {
            if (factor === 'Viral Potential') return [168, 85, 247];
            if (factor === 'High') return [234, 179, 8];
            if (factor === 'Medium') return [249, 115, 22];
            return [113, 113, 122];
        };

        // =============================================
        // PAGE 1 — COVER
        // =============================================
        doc.setFillColor(18, 18, 20);
        doc.rect(0, 0, PW, PH, 'F');

        // Accent bar
        const [br, bg, bb] = buzzColor(analysis.PredictedBuzzFactor);
        doc.setFillColor(br, bg, bb);
        doc.rect(0, 0, 6, PH, 'F');

        // Title block
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(28);
        doc.setTextColor(255, 255, 255);
        doc.text('TRAILER', 20, 70);
        doc.text('ANALYSIS', 20, 85);
        doc.text('REPORT', 20, 100);

        doc.setFontSize(11);
        doc.setTextColor(br, bg, bb);
        doc.text((filmData.title || 'Untitled Project').toUpperCase(), 20, 116);

        doc.setFontSize(9);
        doc.setTextColor(120, 120, 130);
        doc.text(`Genre: ${filmData.genre || 'Unspecified'}`, 20, 126);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, 133);
        doc.text('BLACKLIGHT Films  |  BLACKBOXfm Intelligence', 20, 140);

        // Buzz score box
        const score = analysis.PredictedBuzzScore ?? 0;
        doc.setFillColor(30, 30, 34);
        doc.roundedRect(20, 160, 80, 50, 4, 4, 'F');
        doc.setFillColor(br, bg, bb);
        doc.roundedRect(20, 160, 80, 4, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(36);
        doc.setTextColor(255, 255, 255);
        doc.text(`${score}`, 32, 190);
        doc.setFontSize(9);
        doc.setTextColor(br, bg, bb);
        doc.text('BUZZ SCORE', 32, 198);
        doc.setTextColor(160, 160, 170);
        doc.setFontSize(8);
        doc.text(analysis.PredictedBuzzFactor || '', 32, 206);

        // Core concept box
        if (analysis.CoreConcept) {
            doc.setFillColor(30, 30, 34);
            doc.roundedRect(108, 160, 82, 50, 4, 4, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(br, bg, bb);
            doc.text('CORE CONCEPT', 114, 170);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(220, 220, 225);
            const conceptLines = doc.splitTextToSize(analysis.CoreConcept, 68);
            doc.text(conceptLines, 114, 180);
        }

        // Pacing grade
        if (analysis.PacingAssessment) {
            doc.setFillColor(30, 30, 34);
            doc.roundedRect(20, 220, 170, 30, 4, 4, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(br, bg, bb);
            doc.text('PACING GRADE', 28, 230);
            doc.setFontSize(20);
            doc.setTextColor(255, 255, 255);
            doc.text(analysis.PacingAssessment.grade, 28, 244);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(160, 160, 170);
            const paceLines = doc.splitTextToSize(analysis.PacingAssessment.notes || '', 120);
            doc.text(paceLines, 50, 232);
        }

        // Footer
        doc.setFontSize(7);
        doc.setTextColor(60, 60, 70);
        doc.text('CONFIDENTIAL — FOR INTERNAL DEVELOPMENT USE ONLY', 20, 285);

        // =============================================
        // PAGE 2 — OVERALL IMPRESSION + STRENGTHS + WEAKNESSES
        // =============================================
        doc.addPage();
        y = 20;

        sectionHeader('Overall Impression', 30, 30, 34);
        bodyText(analysis.OverallImpression || '');
        y += 4;

        sectionHeader('Key Strengths', 22, 101, 52);
        (analysis.KeyStrengths || []).forEach((s, i) => {
            checkPage(8);
            doc.setFillColor(22, 101, 52);
            doc.circle(margin + 2, y - 0.5, 1.2, 'F');
            bodyText(s, 6);
        });
        y += 4;

        sectionHeader('Weaknesses', 153, 27, 27);
        (analysis.Weaknesses || []).forEach((w) => {
            checkPage(16);
            const sevColors = {
                Critical: [220, 38, 38],
                Moderate: [234, 88, 12],
                Minor: [161, 98, 7],
            };
            const [sr, sg, sb] = sevColors[w.severity] || [100, 100, 100];
            doc.setFillColor(sr, sg, sb);
            doc.roundedRect(margin, y, 18, 5, 1, 1, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6);
            doc.setTextColor(255, 255, 255);
            doc.text((w.severity || '').toUpperCase(), margin + 1.5, y + 3.5);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(30, 30, 30);
            doc.text(w.issue || '', margin + 21, y + 3.5);
            y += 8;
            bodyText(w.detail || '', 4);
            y += 2;
        });

        // =============================================
        // PAGE 3 — PATH TO VIRAL + PLATFORM FIT
        // =============================================
        doc.addPage();
        y = 20;

        sectionHeader('Path to Viral — Upgrade Roadmap', 88, 28, 135);
        (analysis.HighBuzzRoadmap || []).forEach((step, i) => {
            checkPage(18);
            doc.setFillColor(88, 28, 135);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(88, 28, 135);
            doc.text(`${String(i + 1).padStart(2, '0')}`, margin, y + 3.5);
            doc.setTextColor(20, 20, 20);
            doc.text(step.action || '', margin + 8, y + 3.5);
            y += 7;
            bodyText(step.detail || '', 8);
            y += 2;
        });
        y += 4;

        sectionHeader('Platform Fit Scores', 14, 116, 144);
        const platforms = analysis.PlatformFitScores || {};
        const platformColors = {
            YouTube:   [255, 0, 0],
            Instagram: [193, 53, 132],
            TikTok:    [0, 0, 0],
            Facebook:  [24, 119, 242],
            TwitterX:  [0, 0, 0],
        };
        Object.entries(platforms).forEach(([platform, val]) => {
            const [pr, pg, pb] = platformColors[platform] || [100, 100, 100];
            scoreBar(platform, val, pr, pg, pb);
        });

        // =============================================
        // PAGE 4 — AUDIENCE + COMPS + MONEY SHOT + TAGLINES
        // =============================================
        doc.addPage();
        y = 20;

        sectionHeader('Target Audience', 30, 64, 175);
        labeledRow('Primary', analysis.PrimaryAudience);
        y += 2;
        if (analysis.SecondaryAudiences?.length) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text('SECONDARY', margin, y);
            y += 5;
            analysis.SecondaryAudiences.forEach(a => {
                checkPage(6);
                doc.setFillColor(220, 220, 220);
                doc.circle(margin + 2, y - 0.5, 1, 'F');
                bodyText(a, 6, 8);
            });
        }
        y += 6;

        if (analysis.CompetitiveComps?.length) {
            sectionHeader('Competitive Comps', 55, 65, 81);
            analysis.CompetitiveComps.forEach(comp => {
                checkPage(7);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(50, 50, 50);
                doc.text(`▸  ${comp}`, margin + 2, y);
                y += 7;
            });
            y += 4;
        }

        sectionHeader('Money Shot', 161, 118, 0);
        checkPage(20);
        doc.setFillColor(254, 252, 232);
        doc.roundedRect(margin, y, contentW, 18, 2, 2, 'F');
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(120, 80, 0);
        const msLines = doc.splitTextToSize(`"${analysis.MoneyShot || ''}"`, contentW - 8);
        doc.text(msLines, margin + 4, y + 6);
        y += 22;

        sectionHeader('Suggested Taglines', 15, 118, 110);
        (analysis.SuggestedTaglines || []).forEach(t => {
            checkPage(10);
            doc.setFillColor(240, 253, 250);
            doc.roundedRect(margin, y, contentW, 8, 1, 1, 'F');
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(9);
            doc.setTextColor(20, 80, 70);
            const tLines = doc.splitTextToSize(`"${t}"`, contentW - 8);
            doc.text(tLines, margin + 4, y + 5);
            y += tLines.length * 5 + 5;
        });

        // =============================================
        // PAGE 5+ — SPOT BREAKDOWNS (existing logic, polished)
        // =============================================
        doc.addPage();
        y = 20;

        const spots = analysis?.SpotBreakdowns || {};
        const spotColors = { spot60: [6, 182, 212], spot30: [139, 92, 246], spot15: [249, 115, 22] };

        Object.entries(spots).forEach(([key, data]) => {
            checkPage(20);
            const [cr, cg, cb] = spotColors[key] || [100, 100, 100];
            const spotLabel = key.replace('spot', '') + '-SECOND SPOT';

            doc.setFillColor(cr, cg, cb);
            doc.rect(margin, y, 4, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(cr, cg, cb);
            doc.text(spotLabel, margin + 8, y + 7.5);
            y += 14;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text('OBJECTIVE', margin, y);
            y += 5;
            bodyText(data.goal || '', 0, 9);
            y += 4;

            (data.shots || []).forEach((shot, i) => {
                checkPage(10);
                const tc = shot?.timecode ?? null;
                const desc = shot?.description ?? (typeof shot === 'string' ? shot : '');
                doc.setFillColor(cr, cg, cb);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(cr, cg, cb);
                doc.text(String(i + 1).padStart(2, '0'), margin, y + 2.5);
                // Timecode badge
                if (tc) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(7);
                    doc.setTextColor(cr, cg, cb);
                    doc.text(`[${tc}]`, margin + 8, y + 2.5);
                }
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(40, 40, 40);
                const tcOffset = tc ? 22 : 8;
                const sLines = doc.splitTextToSize(desc, contentW - tcOffset);
                doc.text(sLines, margin + tcOffset, y + 2.5);
                y += sLines.length * 5 + 3;
            });
            y += 10;
        });

        // Page numbers on every page
        const totalPages = doc.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
            doc.setPage(p);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(150, 150, 160);
            if (p > 1) {
                doc.text(`${filmData.title || 'Trailer Analysis'}  |  Page ${p} of ${totalPages}`, margin, PH - 8);
            }
        }

        doc.save(`${(filmData.title || 'Trailer').replace(/\s+/g, '_')}_Analysis_Report.pdf`);
        setNotification({ type: 'success', message: 'Analysis report exported.' });
    } catch (error) {
        console.error('Report Export Error:', error);
        setNotification({ type: 'error', message: 'Failed to generate report.' });
    }
  };

  const handleDeleteAnalysis = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      if (storage && filmData?.video_keyframes_urls?.length > 0) {
        const deletePromises = filmData.video_keyframes_urls.map(async (url) => {
          try {
            const decodedUrl = decodeURIComponent(url);
            const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
            if (pathMatch?.[1]) {
              const fileRef = ref(storage, pathMatch[1]);
              await deleteObject(fileRef);
            }
          } catch (err) {
            console.warn('Storage delete skipped:', err.code);
          }
        });
        await Promise.allSettled(deletePromises);
      }
      const updatedData = { ...filmData, video_analysis: null, video_keyframes_urls: null };
      await saveFilmData(updatedData);
      setFrames([]);
      setVideoFile(null);
      setShowDeleteModal(false);
      setNotification({ type: 'info', message: 'Analysis data cleared.' });
    } catch (err) {
      setNotification({ type: 'error', message: 'Reset failed. Please try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="h-full flex flex-col bg-zinc-950">
        <PremiumVideoOverlay isVisible={loading} currentStepIndex={loadingStepIndex} progressPercent={progress} />

        {/* ── TOOLBAR ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 flex-shrink-0">
            <div className="flex items-center gap-3">
                <div className="p-1.5 bg-cyan-900/20 rounded border border-cyan-500/30">
                    <MonitorPlay size={16} className="text-cyan-400" />
                </div>
                <div>
                    <h2 className="text-sm font-bold text-white font-poppins tracking-tight leading-none">Screening Room</h2>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">Source Monitor & Analysis</p>
                </div>
            </div>
            {analysis && (
                <div className="flex gap-2">
                    <AppButton onClick={handleExportAnalysisReport} variant="secondary" size="sm" icon={FileText}>Export Report</AppButton>
                    <AppButton onClick={() => setShowDeleteModal(true)} variant="danger" size="sm" icon={Trash2}>Reset</AppButton>
                </div>
            )}
        </div>

        {/* ── MAIN BODY ── */}
        <div className="flex-1 flex overflow-hidden min-h-0">

            {/* ── LEFT COLUMN: monitor + scrubber + spot editor ── */}
            <div className="flex flex-col flex-1 min-w-0 border-r border-zinc-800">

                {/* SOURCE MONITOR */}
                <div className="flex-1 bg-black flex items-center justify-center relative overflow-hidden group min-h-0">
                    {frames.length > 0 ? (
                        <>
                            <img src={frames[activeFrameIndex]} alt="Monitor" className="max-w-full max-h-full object-contain" />
                            <div className="absolute top-3 left-3">
                                <TimecodeDisplay frameCount={activeFrameIndex * 15} />
                            </div>
                            <div className="absolute top-3 right-3 text-[10px] font-mono text-zinc-400 bg-black/60 px-2 py-1 rounded border border-zinc-700/40">
                                FRAME {String(activeFrameIndex + 1).padStart(2,'0')} / {frames.length}
                            </div>
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] text-zinc-600 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                ← → Arrow keys to scrub
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-zinc-500 p-8">
                            <Aperture size={40} className="mx-auto mb-4 opacity-20" />
                            <p className="font-mono text-xs mb-6">NO SOURCE SIGNAL</p>
                            <label className="inline-flex flex-col items-center px-6 py-3 bg-zinc-900 border border-zinc-700 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors group">
                                <span className="text-xs font-bold text-zinc-400 group-hover:text-cyan-400 flex items-center gap-2">
                                    <UploadCloud size={13} /> IMPORT MEDIA
                                </span>
                                <input type='file' className="hidden" onChange={handleFileChange} accept="video/*" />
                            </label>
                            {(videoFile || videoUrl) && !loading && (
                                <div className="mt-4">
                                    <p className="text-xs text-cyan-400 mb-2">Queued: {videoFile?.name || "URL Stream"}</p>
                                    <button onClick={handleAnalyze} className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded shadow-[0_0_15px_rgba(8,145,178,0.4)] transition-all">
                                        INITIALIZE SCAN
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* SCRUBBER */}
                {frames.length > 0 && (
                    <div className="h-20 bg-zinc-900 border-t border-zinc-800 flex items-center px-2 gap-1 overflow-x-auto custom-scrollbar flex-shrink-0" ref={scrollContainerRef}>
                        {frames.map((frame, i) => (
                            <button key={i} onClick={() => setActiveFrameIndex(i)}
                                className={`flex-shrink-0 h-full aspect-video rounded-sm overflow-hidden border-2 transition-all ${activeFrameIndex === i ? 'border-cyan-500 scale-105 z-10 shadow-lg shadow-cyan-900/50' : 'border-transparent opacity-40 hover:opacity-80'}`}>
                                <img src={frame} className="w-full h-full object-cover" alt="scrub" />
                            </button>
                        ))}
                    </div>
                )}

                {/* SPOT EDITOR — bottom panel */}
                {analysis?.SpotBreakdowns && (
                    <div className="h-64 border-t border-zinc-800 bg-zinc-950 flex flex-col flex-shrink-0">
                        {/* Spot tabs + export */}
                        <div className="flex items-center justify-between border-b border-zinc-800 flex-shrink-0 pr-4">
                            <div className="flex">
                                {Object.keys(analysis.SpotBreakdowns).map(key => {
                                    const label = key.replace('spot','');
                                    const accent = key === 'spot60' ? 'text-cyan-400 border-cyan-500' : key === 'spot30' ? 'text-purple-400 border-purple-500' : 'text-orange-400 border-orange-500';
                                    return (
                                        <button key={key} onClick={() => setActiveSpotTab(key)}
                                            className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest border-r border-zinc-800 transition-colors flex items-center gap-1.5 ${activeSpotTab === key ? `bg-zinc-800 ${accent} border-b-2` : 'text-zinc-500 hover:text-zinc-300'}`}>
                                            <Scissors size={10} /> {label}s
                                        </button>
                                    );
                                })}
                            </div>
                            <button onClick={handleExportShotList} className="text-[10px] font-bold text-zinc-500 hover:text-white flex items-center gap-1 uppercase tracking-widest">
                                <Download size={11} /> Export EDL
                            </button>
                        </div>

                        {/* Shot list */}
                        {analysis.SpotBreakdowns[activeSpotTab] && (
                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                {/* Objective bar */}
                                <div className="px-4 py-2 border-b border-zinc-800/60 flex items-center gap-3 bg-zinc-900/40 flex-shrink-0">
                                    <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest shrink-0">Objective</span>
                                    <p className="text-xs text-zinc-300 leading-tight">{analysis.SpotBreakdowns[activeSpotTab].goal}</p>
                                </div>
                                {/* Shot rows */}
                                <div>
                                    {(analysis.SpotBreakdowns[activeSpotTab].shots || []).map((shot, i) => {
                                        const tc = shot?.timecode ?? null;
                                        const desc = shot?.description ?? (typeof shot === 'string' ? shot : '');
                                        const accentBar = activeSpotTab === 'spot60' ? 'bg-cyan-500' : activeSpotTab === 'spot30' ? 'bg-purple-500' : 'bg-orange-500';
                                        return (
                                            <div key={i} className="flex items-stretch group border-b border-zinc-800/40 hover:bg-zinc-800/40 transition-colors">
                                                {/* Shot number */}
                                                <div className="w-10 flex items-center justify-center text-[10px] font-mono text-zinc-600 group-hover:text-cyan-400 shrink-0 border-r border-zinc-800/40">
                                                    {String(i + 1).padStart(2, '0')}
                                                </div>
                                                {/* Accent line */}
                                                <div className={`w-0.5 ${accentBar} opacity-0 group-hover:opacity-100 transition-opacity`} />
                                                {/* Timecode */}
                                                {tc && (
                                                    <div className="flex items-center px-2 shrink-0">
                                                        <span className="font-mono text-[9px] text-cyan-500/80 bg-cyan-950/30 px-1.5 py-0.5 rounded border border-cyan-900/40 whitespace-nowrap">{tc}</span>
                                                    </div>
                                                )}
                                                {/* Description */}
                                                <div className="flex-1 py-2 px-3 text-xs text-zinc-400 group-hover:text-zinc-200 leading-relaxed transition-colors">
                                                    {desc}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── RIGHT COLUMN: tabbed intelligence panel ── */}
            {analysis && (
                <div className="w-80 xl:w-96 flex flex-col bg-zinc-900 flex-shrink-0 overflow-hidden">

                    {/* Intel tab bar */}
                    <div className="flex border-b border-zinc-800 flex-shrink-0 overflow-x-auto">
                        {[
                            { id: 'overview', label: 'Overview', icon: Film },
                            { id: 'signals', label: 'Signals', icon: Zap },
                            { id: 'platform', label: 'Platform', icon: Globe },
                            { id: 'audience', label: 'Audience', icon: Users },
                        ].map(({ id, label, icon: Icon }) => (
                            <button key={id} onClick={() => setActiveIntelTab(id)}
                                className={`flex items-center gap-1.5 px-3 py-2.5 text-[9px] font-bold uppercase tracking-widest whitespace-nowrap border-r border-zinc-800 transition-colors flex-1 justify-center ${activeIntelTab === id ? 'bg-zinc-800 text-white border-b-2 border-b-cyan-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
                                <Icon size={9} /> {label}
                            </button>
                        ))}
                    </div>

                    {/* Intel content */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-5">

                        {/* ── OVERVIEW TAB ── */}
                        {activeIntelTab === 'overview' && (
                            <>
                                {/* Buzz Score */}
                                <InspectorItem label="Predicted Buzz" icon={TrendingUp}>
                                    {(() => {
                                        const score = analysis.PredictedBuzzScore ?? 50;
                                        const factor = analysis.PredictedBuzzFactor;
                                        const barColor = factor === 'Viral Potential' ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                                            : factor === 'High' ? 'bg-gradient-to-r from-yellow-500 to-orange-400'
                                            : factor === 'Medium' ? 'bg-yellow-600' : 'bg-zinc-600';
                                        const badgeColor = factor === 'Viral Potential' ? 'text-pink-400 border-pink-500/40 bg-pink-900/20'
                                            : factor === 'High' ? 'text-yellow-400 border-yellow-500/40 bg-yellow-900/20'
                                            : factor === 'Medium' ? 'text-orange-400 border-orange-500/40 bg-orange-900/20'
                                            : 'text-zinc-400 border-zinc-600 bg-zinc-800';
                                        return (
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${badgeColor}`}>{factor}</span>
                                                    <span className="font-mono font-bold text-white text-xl">{score}<span className="text-zinc-500 text-xs">/100</span></span>
                                                </div>
                                                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${barColor}`} style={{width:`${score}%`}}></div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </InspectorItem>

                                {/* Overall impression */}
                                <InspectorItem label="Overall Impression" icon={Film}>
                                    <p className="text-zinc-300 text-xs leading-relaxed">{analysis.OverallImpression}</p>
                                    {analysis.CoreConcept && (
                                        <div className="mt-2 px-2 py-1 bg-cyan-950/40 border border-cyan-800/30 rounded text-cyan-300 text-[10px] font-mono">
                                            ▸ {analysis.CoreConcept}
                                        </div>
                                    )}
                                </InspectorItem>

                                {/* Pacing */}
                                {analysis.PacingAssessment && (
                                    <InspectorItem label="Pacing" icon={Activity}>
                                        <div className="flex items-start gap-3">
                                            {(() => {
                                                const g = analysis.PacingAssessment.grade;
                                                const gc = g === 'A' ? 'text-green-400 border-green-500/40 bg-green-900/20'
                                                    : g === 'B' ? 'text-cyan-400 border-cyan-500/40 bg-cyan-900/20'
                                                    : g === 'C' ? 'text-yellow-400 border-yellow-500/40 bg-yellow-900/20'
                                                    : g === 'D' ? 'text-orange-400 border-orange-500/40 bg-orange-900/20'
                                                    : 'text-red-400 border-red-500/40 bg-red-900/20';
                                                return <span className={`text-2xl font-black font-mono px-3 py-1 rounded border shrink-0 ${gc}`}>{g}</span>;
                                            })()}
                                            <p className="text-zinc-400 text-xs leading-relaxed">{analysis.PacingAssessment.notes}</p>
                                        </div>
                                    </InspectorItem>
                                )}

                                {/* Money Shot */}
                                <InspectorItem label="Money Shot" icon={Star}>
                                    <div className="p-2.5 bg-black/40 rounded border border-zinc-700 text-cyan-300 italic text-xs leading-relaxed">
                                        "{analysis.MoneyShot}"
                                    </div>
                                </InspectorItem>

                                {/* Taglines */}
                                {analysis.SuggestedTaglines?.length > 0 && (
                                    <InspectorItem label="Taglines" icon={Lightbulb}>
                                        <div className="flex flex-wrap gap-1.5">
                                            {analysis.SuggestedTaglines.map((t, i) => (
                                                <span key={i} className="text-[10px] bg-zinc-800 border border-zinc-700 px-2 py-1 rounded text-zinc-300 leading-relaxed">{t}</span>
                                            ))}
                                        </div>
                                    </InspectorItem>
                                )}

                                {/* Comps */}
                                {analysis.CompetitiveComps?.length > 0 && (
                                    <InspectorItem label="Comparable Trailers" icon={Clapperboard}>
                                        <ul className="space-y-1">
                                            {analysis.CompetitiveComps.map((comp, i) => (
                                                <li key={i} className="flex items-center gap-2 text-zinc-300 text-xs">
                                                    <ChevronRight size={9} className="text-zinc-600 shrink-0" /> {comp}
                                                </li>
                                            ))}
                                        </ul>
                                    </InspectorItem>
                                )}
                            </>
                        )}

                        {/* ── SIGNALS TAB ── */}
                        {activeIntelTab === 'signals' && (
                            <>
                                {/* Strengths */}
                                <InspectorItem label="Key Strengths" icon={ThumbsUp}>
                                    <ul className="space-y-2">
                                        {analysis.KeyStrengths?.map((s, i) => (
                                            <li key={i} className="flex items-start gap-2 text-zinc-300 text-xs">
                                                <CheckCircle size={11} className="text-green-500 mt-0.5 shrink-0" /> {s}
                                            </li>
                                        ))}
                                    </ul>
                                </InspectorItem>

                                {/* Weaknesses */}
                                {analysis.Weaknesses?.length > 0 && (
                                    <InspectorItem label="Weaknesses" icon={ShieldAlert}>
                                        <ul className="space-y-3">
                                            {analysis.Weaknesses.map((w, i) => {
                                                const sev = w.severity;
                                                const sc = sev === 'Critical' ? 'text-red-400 bg-red-900/20 border-red-500/30'
                                                    : sev === 'Moderate' ? 'text-orange-400 bg-orange-900/20 border-orange-500/30'
                                                    : 'text-yellow-500 bg-yellow-900/20 border-yellow-500/30';
                                                return (
                                                    <li key={i} className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${sc}`}>{sev}</span>
                                                            <span className="text-white text-xs font-bold">{w.issue}</span>
                                                        </div>
                                                        <p className="text-zinc-400 text-xs leading-relaxed pl-1">{w.detail}</p>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </InspectorItem>
                                )}

                                {/* Roadmap */}
                                {analysis.HighBuzzRoadmap?.length > 0 && (
                                    <InspectorItem label="Path to Viral" icon={Rocket}>
                                        <ul className="space-y-3">
                                            {analysis.HighBuzzRoadmap.map((step, i) => (
                                                <li key={i} className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[9px] font-black text-purple-400 font-mono shrink-0">{String(i+1).padStart(2,'0')}</span>
                                                        <span className="text-purple-300 text-xs font-bold">{step.action}</span>
                                                    </div>
                                                    <p className="text-zinc-400 text-xs leading-relaxed pl-5">{step.detail}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    </InspectorItem>
                                )}
                            </>
                        )}

                        {/* ── PLATFORM TAB ── */}
                        {activeIntelTab === 'platform' && (
                            <>
                                {analysis.PlatformFitScores && (
                                    <InspectorItem label="Platform Fit Scores" icon={Globe}>
                                        <div className="space-y-3">
                                            {Object.entries(analysis.PlatformFitScores).map(([platform, score]) => {
                                                const barColor = score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-zinc-600';
                                                const scoreColor = score >= 75 ? 'text-green-400' : score >= 50 ? 'text-yellow-400' : 'text-zinc-500';
                                                return (
                                                    <div key={platform}>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="text-[10px] text-zinc-400 font-mono">{platform}</span>
                                                            <span className={`text-xs font-bold font-mono ${scoreColor}`}>{score}</span>
                                                        </div>
                                                        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{width:`${score}%`}}></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </InspectorItem>
                                )}

                                {/* Comps in platform context */}
                                {analysis.CompetitiveComps?.length > 0 && (
                                    <InspectorItem label="Benchmark Comps" icon={BarChart2}>
                                        <ul className="space-y-1.5">
                                            {analysis.CompetitiveComps.map((comp, i) => (
                                                <li key={i} className="flex items-center gap-2 text-zinc-300 text-xs">
                                                    <Target size={9} className="text-cyan-600 shrink-0" /> {comp}
                                                </li>
                                            ))}
                                        </ul>
                                    </InspectorItem>
                                )}
                            </>
                        )}

                        {/* ── AUDIENCE TAB ── */}
                        {activeIntelTab === 'audience' && (
                            <>
                                {analysis.PrimaryAudience && (
                                    <InspectorItem label="Primary Audience" icon={Users}>
                                        <p className="text-zinc-300 text-xs leading-relaxed">{analysis.PrimaryAudience}</p>
                                    </InspectorItem>
                                )}
                                {analysis.SecondaryAudiences?.length > 0 && (
                                    <InspectorItem label="Secondary Segments" icon={Users}>
                                        <div className="flex flex-wrap gap-1.5">
                                            {analysis.SecondaryAudiences.map((a, i) => (
                                                <span key={i} className="text-[9px] bg-zinc-800 border border-zinc-700 px-2 py-1 rounded text-zinc-400">{a}</span>
                                            ))}
                                        </div>
                                    </InspectorItem>
                                )}
                                {analysis.SuggestedTaglines?.length > 0 && (
                                    <InspectorItem label="Messaging / Taglines" icon={Lightbulb}>
                                        <ul className="space-y-2">
                                            {analysis.SuggestedTaglines.map((t, i) => (
                                                <li key={i} className="text-[10px] bg-zinc-800/60 border border-zinc-700/60 px-2.5 py-1.5 rounded text-zinc-300 leading-relaxed italic">
                                                    "{t}"
                                                </li>
                                            ))}
                                        </ul>
                                    </InspectorItem>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>

        {/* ── DELETE MODAL ── */}
        {showDeleteModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 max-w-sm w-full text-center">
                    {isDeleting ? (
                        <>
                            <Loader2 className="w-10 h-10 text-cyan-400 mx-auto mb-4 animate-spin" />
                            <h3 className="text-white font-bold mb-2">Resetting...</h3>
                            <p className="text-zinc-400 text-sm">Clearing frames and analysis data.</p>
                        </>
                    ) : (
                        <>
                            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                            <h3 className="text-white font-bold mb-2">Reset Screening Room?</h3>
                            <p className="text-zinc-400 text-sm mb-6">This will delete all extracted frames and analysis data.</p>
                            <div className="flex justify-center gap-3">
                                <AppButton onClick={() => setShowDeleteModal(false)} variant="secondary">Cancel</AppButton>
                                <AppButton onClick={handleDeleteAnalysis} variant="danger">Reset</AppButton>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )}
    </div>
  );
};

export default VideoAnalysisTab;